/**
 * Acquisition first-touch capture (Phase 1 of the ops data plan).
 *
 * Public-site service. Has zero dependencies on Firebase Auth or Firestore
 * because it must run on the homepage and on landing pages BEFORE the
 * visitor signs up. The persisted side effect is a single first-party
 * cookie (`melio_firsttouch`); Firestore writes happen later in the
 * `completeSignup` Cloud Function (see CTO brief §4.3).
 *
 * Semantics:
 *   - First touch is FIRST TOUCH. We never overwrite an existing cookie,
 *     so the homepage `/` recording does not stomp a `/c/rangan` recording
 *     a user did three weeks earlier (and vice versa).
 *   - If the user declines marketing-cookie consent, no cookie is written
 *     and signup falls through to `channel: 'direct'`.
 *
 * See: docs/markdown/eng/CTO_BRIEF_ACQUISITION_TRACKING.md §4.2
 */

import type {
  AcquisitionChannel,
  CampaignDoc,
  FirstTouchPayload,
  FirstTouchUtm,
} from '../types/acquisition';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const FIRST_TOUCH_COOKIE = 'melio_firsttouch';

/**
 * 180-day cookie. Matches the typical B2C health-purchase consideration
 * window (podcast listen → research → trial → buy). Long enough that a
 * podcast-driven prospect who returns 3 months later still gets attributed.
 */
const COOKIE_DAYS = 180;

const COOKIE_PATH = '/';

const UTM_KEYS: readonly (keyof FirstTouchUtm)[] = [
  'source',
  'medium',
  'campaign',
  'content',
  'term',
] as const;

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Record a first-touch from a campaign landing page (e.g. `/c/rangan`).
 *
 * Call this on mount of CampaignLandingPage. Idempotent: if the cookie
 * already exists, this is a no-op and the prior attribution is preserved.
 *
 * Note: marketing-cookie consent is checked here. If the visitor has
 * declined, we skip the write silently — they'll later flow through
 * signup as `channel: 'direct'` rather than the campaign attribution.
 */
export function recordFirstTouch(campaign?: CampaignDoc | null): void {
  if (!isBrowser()) return;
  if (!hasMarketingCookieConsent()) return;
  if (readCookie(FIRST_TOUCH_COOKIE)) return;

  const utm = readUtmFromUrl();
  const channel: AcquisitionChannel =
    campaign?.channel ?? inferChannelFromUtm(utm) ?? 'direct';

  const payload: FirstTouchPayload = {
    channel,
    campaignId: campaign?.campaignId ?? null,
    firstTouchAt: new Date().toISOString(),
    firstTouchPath: window.location.pathname,
    firstTouchReferrer: extractReferrerHostname(),
    firstTouchUtm: utm,
    promoCode: campaign?.promoCode ?? null,
    referrerUid: null,
    schemaVersion: 1,
  };

  writeCookieJson(FIRST_TOUCH_COOKIE, payload, COOKIE_DAYS);
}

/**
 * Record a first-touch from a referral landing page (e.g. `/invite/abc123`).
 *
 * Always sets channel = `referral` and pins to the `referral_default`
 * campaign id unless caller passes a different one explicitly.
 */
export function recordReferralFirstTouch(
  code: string,
  referrerUid: string,
  options?: { campaignId?: string },
): void {
  if (!isBrowser()) return;
  if (!hasMarketingCookieConsent()) return;
  if (readCookie(FIRST_TOUCH_COOKIE)) return;

  const payload: FirstTouchPayload = {
    channel: 'referral',
    campaignId: options?.campaignId ?? 'referral_default',
    firstTouchAt: new Date().toISOString(),
    firstTouchPath: `/invite/${code}`,
    firstTouchReferrer: extractReferrerHostname(),
    firstTouchUtm: {},
    promoCode: code,
    referrerUid,
    schemaVersion: 1,
  };

  writeCookieJson(FIRST_TOUCH_COOKIE, payload, COOKIE_DAYS);
}

/**
 * Read the cached first-touch payload to send with the signup callable.
 * Returns null if no cookie set (visitor declined consent or arrived
 * directly without ever hitting a recording surface).
 */
export function readFirstTouchForSignup(): FirstTouchPayload | null {
  if (!isBrowser()) return null;
  const raw = readCookie(FIRST_TOUCH_COOKIE);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as FirstTouchPayload;
    if (parsed && typeof parsed === 'object' && parsed.schemaVersion === 1) {
      return parsed;
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Clear the first-touch cookie. Used by GDPR erasure flows; in normal
 * operation the cookie expires naturally after `COOKIE_DAYS`.
 */
export function clearFirstTouch(): void {
  if (!isBrowser()) return;
  document.cookie = `${FIRST_TOUCH_COOKIE}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${COOKIE_PATH}`;
}

// ---------------------------------------------------------------------------
// Internals
// ---------------------------------------------------------------------------

function isBrowser(): boolean {
  return typeof window !== 'undefined' && typeof document !== 'undefined';
}

/**
 * Returns whether the visitor has consented to the marketing-cookie
 * category. Today this hooks into the existing cookie-consent banner via
 * `localStorage`; if the banner ever moves to a dedicated consent
 * service we centralise the lookup here.
 *
 * Default policy: until the consent banner is wired up to expose a
 * `marketing` category, we treat absence of an explicit decline as
 * acceptance, which matches the current Melio site's posture toward
 * first-party analytics. Update this function once the banner ships.
 */
function hasMarketingCookieConsent(): boolean {
  if (!isBrowser()) return false;
  try {
    const raw = window.localStorage.getItem('melio_cookie_consent_v1');
    if (!raw) return true;
    const parsed = JSON.parse(raw) as { marketing?: boolean };
    return parsed?.marketing !== false;
  } catch {
    return true;
  }
}

function readCookie(name: string): string | null {
  if (!isBrowser()) return null;
  const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const match = document.cookie.match(new RegExp(`(?:^|; )${escaped}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

function writeCookieJson(name: string, value: object, days: number): void {
  if (!isBrowser()) return;
  const expires = new Date(Date.now() + days * 86400000).toUTCString();
  const encoded = encodeURIComponent(JSON.stringify(value));
  // SameSite=Lax so the cookie survives top-level navigation from a
  // podcast host's site. Secure so it's only sent over HTTPS in
  // production. We do not set Domain explicitly so the cookie stays
  // bound to the exact host (meliohealthmgmt.co.uk).
  document.cookie =
    `${name}=${encoded}; expires=${expires}; path=${COOKIE_PATH}; SameSite=Lax; Secure`;
}

function readUtmFromUrl(): FirstTouchUtm {
  if (!isBrowser()) return {};
  try {
    const params = new URLSearchParams(window.location.search);
    const utm: FirstTouchUtm = {};
    for (const key of UTM_KEYS) {
      const value = params.get(`utm_${key}`);
      if (value) utm[key] = value.slice(0, 80);
    }
    return utm;
  } catch {
    return {};
  }
}

/**
 * Best-effort channel inference from UTM medium when no explicit campaign
 * is loaded (i.e. the visitor landed on a non-`/c/...` URL with utm_*
 * parameters). Conservative: if we can't tell, we return null and the
 * caller falls back to `direct`.
 */
function inferChannelFromUtm(utm: FirstTouchUtm): AcquisitionChannel | null {
  const medium = (utm.medium || '').toLowerCase();
  const source = (utm.source || '').toLowerCase();
  if (!medium && !source) return null;
  if (medium === 'podcast' || source.includes('podcast')) return 'podcast';
  if (medium === 'referral') return 'referral';
  if (medium === 'organic' || medium === 'seo' || medium === 'content') return 'content';
  if (medium === 'partnership' || medium === 'partner') return 'partnership';
  if (medium === 'pr' || medium === 'press') return 'pr';
  if (medium === 'cpc' || medium === 'paid' || medium === 'ppc') return 'paid';
  return 'other';
}

/**
 * Privacy: store referrer HOSTNAME only, never full URL with query string.
 * `https://chatterjee.com/episode/123?utm_internal=foo` becomes
 * `chatterjee.com`, which is enough to attribute the source without
 * carrying a query string that may contain personal data.
 */
function extractReferrerHostname(): string | null {
  if (!isBrowser()) return null;
  const referrer = document.referrer;
  if (!referrer) return null;
  try {
    return new URL(referrer).hostname || null;
  } catch {
    return null;
  }
}
